import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CellTowerIcon from "@mui/icons-material/CellTower";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NewReleasesOutlinedIcon from "@mui/icons-material/NewReleasesOutlined";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import SyncIcon from "@mui/icons-material/Sync";
import UpdateIcon from "@mui/icons-material/Update";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import AlertCard from "src/Components/AlertCard";
import {
  MonitoringAlert,
  MonitoringAlertFeed,
  MonitoringAlertType,
  MonitoringListingProvider,
  MonitoringScanRun,
  MonitoringScanStatusSummary,
} from "src/Types/alerts.types";
import { MonitoringConfigurationInput } from "src/Types/monitoring.types";
import {
  acknowledgeMonitoringAlert,
  getMonitoringAlerts,
  getMonitoringScanStatus,
  startMonitoringScan,
} from "src/utils/apicalls";
import { generateMonitoringReport } from "src/services/pdfReportService";
import { transformToReportData } from "src/utils/reportDataTransformer";
import { formatRelativeTime, formatShortDuration } from "src/i18n/format";
import i18n from "src/i18n";
import { useLocale } from "src/i18n/LocaleProvider";
import { getApiErrorMessage } from "src/utils/apiError";
import { getAlertDetail, getAlertText } from "./alertText";

interface AlertsSidebarProps {
  enabled: boolean;
  variant?: "sidebar" | "embedded";
  selectedAlertId: string | null;
  mapContainerRef?: React.RefObject<HTMLDivElement>;
  configuration?: MonitoringConfigurationInput;
  canScan?: boolean;
  onSelectAlert: (alert: MonitoringAlert) => void;
  onFeedChange: (feed: MonitoringAlertFeed) => void;
}

export interface AlertAreaGroup {
  key: string;
  label: string;
  position: number;
  activity: boolean;
  alerts: MonitoringAlert[];
  inside: MonitoringAlert[];
  nearby: MonitoringAlert[];
}

const EMPTY_FEED: MonitoringAlertFeed = {
  alerts: [],
  latestScan: null,
  unreadCount: 0,
};

const ACTIVITY_KEY = "monitoring-activity";
const ACTIVE_SCAN_POLL_MS = 5000;
const IDLE_POLL_MS = 60000;

const isActiveScan = (scan: MonitoringScanRun | null) =>
  scan?.status === "QUEUED" || scan?.status === "RUNNING";

export const scanStatusSignature = ({
  latestScan,
  latestAlertId,
}: MonitoringScanStatusSummary) =>
  [
    latestScan?.id ?? "",
    latestScan?.status ?? "",
    latestScan?.updatedAt ?? "",
    latestAlertId ?? "",
  ].join("|");
const AIRBNB_ACCENT = "#f43f5e";
const VRBO_ACCENT = "#f59e0b";

export const listingProvider = (
  alert: MonitoringAlert
): MonitoringListingProvider =>
  alert.listing?.provider === "VRBO" ? "VRBO" : "AIRBNB";

export const isNearbyAlert = (alert: MonitoringAlert) => {
  if (alert.listing?.matchStatus === "NEARBY") return true;
  if (alert.listing?.matchStatus === "INSIDE") return false;
  return alert.type === "NEARBY_LISTING";
};

export const splitByProvider = (alerts: MonitoringAlert[]) => {
  const airbnb: MonitoringAlert[] = [];
  const vrbo: MonitoringAlert[] = [];
  alerts.forEach((alert) => {
    if (listingProvider(alert) === "VRBO") vrbo.push(alert);
    else airbnb.push(alert);
  });
  return { airbnb, vrbo };
};

export const buildAlertGroups = (
  alerts: MonitoringAlert[],
  activityLabel = i18n.t("dashboard.sectionSystem"),
  areaFallbackLabel = i18n.t("dashboard.monitoringArea")
): AlertAreaGroup[] => {
  const groups = new Map<string, AlertAreaGroup>();

  alerts.forEach((alert) => {
    const activity = !alert.areaKey;
    const key = alert.areaKey || ACTIVITY_KEY;
    const current = groups.get(key);
    if (current) {
      current.alerts.push(alert);
      return;
    }
    groups.set(key, {
      key,
      label: activity ? activityLabel : alert.areaLabel || areaFallbackLabel,
      position: alert.areaPosition ?? Number.MAX_SAFE_INTEGER,
      activity,
      alerts: [alert],
      inside: [],
      nearby: [],
    });
  });

  return Array.from(groups.values())
    .map((group) => {
      if (group.activity) {
        return group;
      }
      const inside: MonitoringAlert[] = [];
      const nearby: MonitoringAlert[] = [];
      group.alerts.forEach((alert) => {
        if (isNearbyAlert(alert)) nearby.push(alert);
        else inside.push(alert);
      });
      return { ...group, inside, nearby };
    })
    .sort(
      (first, second) =>
        Number(first.activity) - Number(second.activity) ||
        first.position - second.position
    );
};

export const listingMetadata = (
  alert: MonitoringAlert,
  t: TFunction = i18n.t
) => {
  const listing = alert.listing;
  if (!listing) return [];
  const locationStatus =
    listing.matchStatus === "NEARBY"
      ? t("dashboard.nearbyDistance", {
          distance: listing.boundaryDistanceMiles?.toFixed(2) || "≤0.25",
        })
      : t("dashboard.insideBoundary");
  const host = listing.hostName || listing.hostId;
  const rating = listing.ratingLabel || listing.rating;
  return [
    { label: t("dashboard.metaJurisdiction"), value: locationStatus },
    ...(host ? [{ label: t("dashboard.metaHost"), value: host }] : []),
    ...(rating ? [{ label: t("dashboard.metaRating"), value: rating }] : []),
    {
      label: t("dashboard.metaFirstDetected"),
      value: formatRelativeTime(listing.firstSeenAt),
    },
    {
      label: t("dashboard.metaListedFor"),
      value: formatShortDuration(
        t,
        listing.currentAppearanceStartedAt,
        listing.active ? undefined : listing.lastSeenAt
      ),
    },
    {
      label: t("dashboard.metaTimesListed"),
      value: String(listing.appearanceCount),
    },
    { label: t("dashboard.metaListingId"), value: listing.providerListingId },
    {
      label: t("dashboard.metaLastObserved"),
      value: formatRelativeTime(listing.lastSeenAt),
    },
  ];
};

const AlertsSidebar: React.FC<AlertsSidebarProps> = ({
  enabled,
  variant = "sidebar",
  selectedAlertId,
  mapContainerRef,
  configuration,
  canScan = false,
  onSelectAlert,
  onFeedChange,
}) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [feed, setFeed] = useState<MonitoringAlertFeed>(EMPTY_FEED);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [error, setError] = useState("");
  /** undefined = use default (first area); null = user collapsed all. */
  const [manualExpandedKey, setManualExpandedKey] = useState<
    string | null | undefined
  >(undefined);
  const [collapsedFenceKeys, setCollapsedFenceKeys] = useState(
    () => new Set<string>()
  );
  const alertRefs = useRef(new Map<string, HTMLDivElement>());
  const loadInFlightRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const feedSignatureRef = useRef<string | null>(null);

  const alertGroups = useMemo(
    () =>
      buildAlertGroups(
        feed.alerts,
        t("dashboard.sectionSystem"),
        t("dashboard.monitoringArea")
      ),
    [feed.alerts, t]
  );
  const areaGroups = useMemo(
    () => alertGroups.filter((group) => !group.activity),
    [alertGroups]
  );
  const activityGroups = useMemo(
    () => alertGroups.filter((group) => group.activity),
    [alertGroups]
  );

  const selectedAreaKey = useMemo(() => {
    if (!selectedAlertId) return null;
    return (
      areaGroups.find((group) =>
        group.alerts.some((alert) => alert.id === selectedAlertId)
      )?.key ?? null
    );
  }, [areaGroups, selectedAlertId]);

  const expandedAreaKey =
    selectedAreaKey ??
    (manualExpandedKey === undefined
      ? areaGroups[0]?.key ?? null
      : manualExpandedKey);

  const selectedFenceKey = useMemo(() => {
    if (!selectedAlertId) return null;
    for (const group of areaGroups) {
      if (group.inside.some((alert) => alert.id === selectedAlertId)) {
        const provider = listingProvider(
          group.inside.find((alert) => alert.id === selectedAlertId)!
        ).toLowerCase();
        return `${group.key}:inside:${provider}`;
      }
      if (group.nearby.some((alert) => alert.id === selectedAlertId)) {
        const provider = listingProvider(
          group.nearby.find((alert) => alert.id === selectedAlertId)!
        ).toLowerCase();
        return `${group.key}:nearby:${provider}`;
      }
    }
    return null;
  }, [areaGroups, selectedAlertId]);

  const selectedParentFenceKey = useMemo(() => {
    if (!selectedFenceKey) return null;
    const parts = selectedFenceKey.split(":");
    if (parts.length < 3) return selectedFenceKey;
    return parts.slice(0, -1).join(":");
  }, [selectedFenceKey]);

  useEffect(() => {
    if (!selectedFenceKey && !selectedParentFenceKey) return;
    setCollapsedFenceKeys((current) => {
      const next = new Set(current);
      let changed = false;
      if (selectedParentFenceKey && next.has(selectedParentFenceKey)) {
        next.delete(selectedParentFenceKey);
        changed = true;
      }
      if (selectedFenceKey && next.has(selectedFenceKey)) {
        next.delete(selectedFenceKey);
        changed = true;
      }
      return changed ? next : current;
    });
  }, [selectedFenceKey, selectedParentFenceKey]);

  useEffect(() => {
    if (!selectedAlertId) return;
    alertRefs.current.get(selectedAlertId)?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [expandedAreaKey, selectedFenceKey, selectedAlertId]);

  const loadFeed = useCallback(async () => {
    if (!enabled) return;
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    if (!hasLoadedRef.current) setLoading(true);
    try {
      const nextFeed = await getMonitoringAlerts();
      hasLoadedRef.current = true;
      feedSignatureRef.current = scanStatusSignature({
        latestScan: nextFeed.latestScan,
        latestAlertId: nextFeed.latestAlertId ?? null,
      });
      setFeed(nextFeed);
      onFeedChange(nextFeed);
      setError("");
      setScanning(isActiveScan(nextFeed.latestScan));
    } catch (requestError: any) {
      setError(getApiErrorMessage(requestError, t, "dashboard.loadError"));
    } finally {
      loadInFlightRef.current = false;
      setLoading(false);
    }
  }, [enabled, onFeedChange, t]);

  const checkScanStatus = useCallback(async () => {
    try {
      const summary = await getMonitoringScanStatus();
      if (scanStatusSignature(summary) !== feedSignatureRef.current) {
        await loadFeed();
      }
    } catch {
      // The next poll retries; the last good feed stays on screen.
    }
  }, [loadFeed]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  useEffect(() => {
    if (!enabled) return;
    const pollMs = scanning ? ACTIVE_SCAN_POLL_MS : IDLE_POLL_MS;
    let timer: number | undefined;
    const stop = () => window.clearInterval(timer);
    const start = () => {
      stop();
      timer = window.setInterval(() => void checkScanStatus(), pollMs);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stop();
        return;
      }
      void checkScanStatus();
      start();
    };
    if (document.visibilityState !== "hidden") start();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkScanStatus, enabled, scanning]);

  const handleScan = async () => {
    setScanning(true);
    setError("");
    try {
      await startMonitoringScan();
      await loadFeed();
    } catch (requestError: any) {
      setScanning(false);
      setError(getApiErrorMessage(requestError, t, "dashboard.scanError"));
    }
  };

  const handleGeneratePdf = async () => {
    if (!mapContainerRef?.current || !configuration) {
      setError(t("dashboard.reportError"));
      return;
    }

    setGeneratingPdf(true);
    setError("");

    try {
      const reportData = transformToReportData(
        feed,
        configuration.mode,
        configuration.community,
        configuration.addresses,
        t
      );

      await generateMonitoringReport(mapContainerRef.current, reportData, {
        t,
        locale,
      });

      setError("");
    } catch (requestError: any) {
      console.error("PDF generation error:", requestError);
      setError(t("dashboard.reportError"));
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSelect = async (alert: MonitoringAlert) => {
    onSelectAlert(alert);
    if (!alert.readAt) {
      setFeed((current) => ({
        ...current,
        unreadCount: Math.max(0, current.unreadCount - 1),
        alerts: current.alerts.map((item) =>
          item.id === alert.id
            ? { ...item, readAt: new Date().toISOString() }
            : item
        ),
      }));
      try {
        await acknowledgeMonitoringAlert(alert.id);
      } catch {
        loadFeed();
      }
    }
  };

  const presentationFor = (
    alert: MonitoringAlert
  ): { accent: string; badge: string; icon: React.ReactNode } => {
    const provider = listingProvider(alert);
    const isVrbo = provider === "VRBO";
    const typePresentation: Record<
      MonitoringAlertType,
      { accent: string; badge: string; icon: React.ReactNode }
    > = {
      NEW_LISTING: {
        accent: isVrbo ? VRBO_ACCENT : AIRBNB_ACCENT,
        badge: isVrbo
          ? t("dashboard.badgeNewVrbo")
          : t("dashboard.badgeNewAirbnb"),
        icon: isVrbo ? (
          <CellTowerIcon fontSize="small" />
        ) : (
          <NewReleasesOutlinedIcon fontSize="small" />
        ),
      },
      NEARBY_LISTING: {
        accent: "#38bdf8",
        badge: t("dashboard.badgeNearby"),
        icon: <NearMeOutlinedIcon fontSize="small" />,
      },
      LISTING_REAPPEARED: {
        accent: "#a78bfa",
        badge: t("dashboard.badgeReappeared"),
        icon: <ReplayIcon fontSize="small" />,
      },
      LISTING_CHANGED: {
        accent: VRBO_ACCENT,
        badge: t("dashboard.badgeChanged"),
        icon: <UpdateIcon fontSize="small" />,
      },
      LISTING_REMOVED: {
        accent: "#60a5fa",
        badge: t("dashboard.badgeRemoved"),
        icon: <ErrorOutlineIcon fontSize="small" />,
      },
      AVAILABILITY_CHANGED: {
        accent: "#fb7185",
        badge: t("dashboard.badgeAvailability"),
        icon: <EventBusyOutlinedIcon fontSize="small" />,
      },
      SCAN_FAILED: {
        accent: AIRBNB_ACCENT,
        badge: t("dashboard.badgeScanFailed"),
        icon: <ErrorOutlineIcon fontSize="small" />,
      },
      SCAN_COMPLETED: {
        accent: "#34d399",
        badge: t("dashboard.badgeScanComplete"),
        icon: <CheckCircleOutlineIcon fontSize="small" />,
      },
    };
    return typePresentation[alert.type];
  };

  const flagFor = (alert: MonitoringAlert) => {
    if (alert.listing?.active === false) return t("dashboard.flagInactive");
    if (alert.type === "NEARBY_LISTING") return t("dashboard.flagNearby");
    if (alert.type === "AVAILABILITY_CHANGED") {
      return t("dashboard.flagAvailability");
    }
    if (alert.type === "LISTING_REAPPEARED") {
      return t("dashboard.flagReappeared");
    }
    return listingProvider(alert) === "VRBO"
      ? t("dashboard.flagVrboMonitoring")
      : t("dashboard.flagAirbnbMonitoring");
  };

  const renderAlertCard = (alert: MonitoringAlert) => {
    const presentation = presentationFor(alert);
    const provider = listingProvider(alert);
    const isVrbo = provider === "VRBO";
    return (
      <Box
        key={alert.id}
        ref={(node: HTMLDivElement | null) => {
          if (node) alertRefs.current.set(alert.id, node);
          else alertRefs.current.delete(alert.id);
        }}
      >
        <AlertCard
          accent={presentation.accent}
          icon={presentation.icon}
          title={getAlertText(alert, t).title}
          badge={presentation.badge}
          detail={getAlertDetail(alert, t)}
          flag={flagFor(alert)}
          disclaimer={
            alert.type === "NEARBY_LISTING"
              ? isVrbo
                ? t("dashboard.nearbyDisclaimerVrbo")
                : t("dashboard.nearbyDisclaimerAirbnb")
              : undefined
          }
          metadata={listingMetadata(alert, t)}
          time={formatRelativeTime(alert.createdAt)}
          unread={!alert.readAt}
          selected={selectedAlertId === alert.id}
          action={
            alert.listing
              ? {
                  label: isVrbo
                    ? t("dashboard.ctaViewVrbo")
                    : t("dashboard.ctaViewAirbnb"),
                  href: alert.listing.url,
                }
              : undefined
          }
          onClick={() => handleSelect(alert)}
        />
      </Box>
    );
  };

  const renderProviderNested = (
    areaKey: string,
    kind: "inside" | "nearby",
    provider: "airbnb" | "vrbo",
    label: string,
    accent: string,
    sectionAlerts: MonitoringAlert[]
  ) => {
    if (sectionAlerts.length === 0) return null;
    const fenceKey = `${areaKey}:${kind}:${provider}`;
    const expanded = !collapsedFenceKeys.has(fenceKey);

    return (
      <Accordion
        key={fenceKey}
        disableGutters
        elevation={0}
        expanded={expanded}
        onChange={(_event, nextExpanded) => {
          setCollapsedFenceKeys((current) => {
            const next = new Set(current);
            if (nextExpanded) next.delete(fenceKey);
            else next.add(fenceKey);
            return next;
          });
        }}
        sx={{
          bgcolor: "transparent",
          boxShadow: "none",
          ml: 0.5,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
          sx={{
            minHeight: 32,
            px: 0.5,
            "& .MuiAccordionSummary-content": {
              my: 0.25,
              alignItems: "center",
              gap: 1,
            },
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: accent,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={800}
            sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
          >
            {label}
          </Typography>
          <Chip size="small" label={sectionAlerts.length} />
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 0.5 }}>
          <Stack spacing={1.25}>{sectionAlerts.map(renderAlertCard)}</Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderFenceSection = (
    areaKey: string,
    kind: "inside" | "nearby",
    label: string,
    sectionAlerts: MonitoringAlert[]
  ) => {
    if (sectionAlerts.length === 0) return null;
    const fenceKey = `${areaKey}:${kind}`;
    const expanded = !collapsedFenceKeys.has(fenceKey);
    const { airbnb, vrbo } = splitByProvider(sectionAlerts);

    return (
      <Accordion
        key={fenceKey}
        disableGutters
        elevation={0}
        expanded={expanded}
        onChange={(_event, nextExpanded) => {
          setCollapsedFenceKeys((current) => {
            const next = new Set(current);
            if (nextExpanded) next.delete(fenceKey);
            else next.add(fenceKey);
            return next;
          });
        }}
        sx={{
          bgcolor: "transparent",
          boxShadow: "none",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
          sx={{
            minHeight: 36,
            px: 0,
            "& .MuiAccordionSummary-content": {
              my: 0.5,
              alignItems: "center",
              gap: 1,
            },
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={800}
          >
            {label}
          </Typography>
          <Chip size="small" label={sectionAlerts.length} />
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0, pb: 0.5 }}>
          <Stack spacing={0.25}>
            {renderProviderNested(
              areaKey,
              kind,
              "airbnb",
              t("dashboard.providerAirbnb"),
              AIRBNB_ACCENT,
              airbnb
            )}
            {renderProviderNested(
              areaKey,
              kind,
              "vrbo",
              t("dashboard.providerVrbo"),
              VRBO_ACCENT,
              vrbo
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box
      component="aside"
      sx={{
        p: variant === "sidebar" ? 2.5 : 2,
        borderRadius: variant === "sidebar" ? 4 : 0,
        border: variant === "sidebar" ? "1px solid" : 0,
        borderColor: "divider",
        bgcolor:
          variant === "sidebar" ? "rgba(15, 23, 42, 0.92)" : "transparent",
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
          <Badge badgeContent={feed.unreadCount} color="error">
            <Typography variant="h6" pr={1}>
              {t("dashboard.alertsTitle")}
            </Typography>
          </Badge>
          <Stack direction="row" spacing={0.5}>
            <Tooltip 
              title={generatingPdf 
                ? t("dashboard.generatingReport")
                : t("dashboard.generateReport")}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  disabled={generatingPdf || feed.alerts.length === 0}
                  onClick={handleGeneratePdf}
                  sx={{
                    color: "primary.main",
                    "&:hover": { bgcolor: "rgba(59, 130, 246, 0.08)" },
                  }}
                >
                  {generatingPdf ? (
                    <CircularProgress size={18} />
                  ) : (
                    <PictureAsPdfIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            {canScan && (
              <Tooltip
                title={scanning ? t("dashboard.scanning") : t("dashboard.scanNow")}
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={scanning}
                    onClick={handleScan}
                    aria-label={t("dashboard.scanNow")}
                    sx={{
                      color: "text.secondary",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    {scanning ? (
                      <CircularProgress size={18} />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block">
          {t("dashboard.alertsSubtitle")}
        </Typography>
      </Box>

      {feed.latestScan && (
        <Stack direction="row" spacing={1} alignItems="center" mt={2}>
          <SyncIcon
            fontSize="small"
            color={
              feed.latestScan.status === "FAILED" ? "error" : "action"
            }
          />
          <Typography variant="caption" color="text.secondary">
            {t("dashboard.latestScan", {
              status: t(`dashboard.scanStatus.${feed.latestScan.status}`),
              count: feed.latestScan.listingCount,
            })}
          </Typography>
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
        {loading && feed.alerts.length === 0 && (
          <Stack alignItems="center" spacing={1.5} py={6}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              {t("dashboard.loadingAlerts")}
            </Typography>
          </Stack>
        )}
        {!loading && !error && feed.alerts.length === 0 && (
          <Box textAlign="center" py={6}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
            <Typography fontWeight={700}>{t("dashboard.noAlertsTitle")}</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {t("dashboard.noAlertsBody")}
            </Typography>
          </Box>
        )}

        {areaGroups.map((group) => (
          <Accordion
            key={group.key}
            disableGutters
            elevation={0}
            expanded={expandedAreaKey === group.key}
            onChange={(_event, expanded) =>
              setManualExpandedKey(expanded ? group.key : null)
            }
            sx={{
              bgcolor: "rgba(15, 23, 42, 0.55)",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "12px !important",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
              sx={{
                px: 1.5,
                minHeight: 48,
                "& .MuiAccordionSummary-content": {
                  my: 1,
                  alignItems: "center",
                  gap: 1,
                },
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={800}
                sx={{ lineHeight: 1.4 }}
              >
                {group.label}
              </Typography>
              <Chip size="small" label={group.alerts.length} />
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
              <Stack spacing={0.5}>
                {renderFenceSection(
                  group.key,
                  "inside",
                  t("dashboard.insideFence"),
                  group.inside
                )}
                {renderFenceSection(
                  group.key,
                  "nearby",
                  t("dashboard.nearbyFence"),
                  group.nearby
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}

        {activityGroups.map((group) => (
          <Accordion
            key={group.key}
            disableGutters
            elevation={0}
            defaultExpanded
            sx={{
              bgcolor: "rgba(15, 23, 42, 0.55)",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "12px !important",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
              sx={{
                px: 1.5,
                minHeight: 48,
                "& .MuiAccordionSummary-content": {
                  my: 1,
                  alignItems: "center",
                  gap: 1,
                },
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={800}
                sx={{ lineHeight: 1.4 }}
              >
                {group.label}
              </Typography>
              <Chip size="small" label={group.alerts.length} />
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
              <Stack spacing={1.25}>{group.alerts.map(renderAlertCard)}</Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Box>
  );
};

export default AlertsSidebar;
